import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from './stores/auth.js'

// Pages
import LoginPage from './pages/LoginPage.jsx'
import RegisterPage from './pages/RegisterPage.jsx'
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx'
import ResetPasswordPage from './pages/ResetPasswordPage.jsx'
import DashboardPage from './pages/DashboardPage.jsx'
import TreePage from './pages/TreePage.jsx'
import PersonPage from './pages/PersonPage.jsx'
import AddPersonPage from './pages/AddPersonPage.jsx'
import AdminPage from './pages/AdminPage.jsx'
import ProfilePage from './pages/ProfilePage.jsx'
import InvitePage from './pages/InvitePage.jsx'
import HelpPage from './pages/HelpPage.jsx'
import ArchivePage from './pages/ArchivePage.jsx'
import MyJourneyPage from './pages/MyJourneyPage.jsx'
import BillingPage from './pages/BillingPage.jsx'
import ClaimRecordPage from './pages/ClaimRecordPage.jsx'
import SuggestionsInboxPage from './pages/SuggestionsInboxPage.jsx'
import WorldMapPage from './pages/WorldMapPage.jsx'
import OnboardingPage from './pages/OnboardingPage.jsx'
import MysteriesPage from './pages/MysteriesPage.jsx'
import ResearcherProfilePage from './pages/ResearcherProfilePage.jsx'
import LeaderboardPage from './pages/LeaderboardPage.jsx'
import ChangelogPage from './pages/ChangelogPage.jsx'

// Layout
import AppShell from './components/shared/AppShell.jsx'

function PrivateRoute({ children }) {
  const token = useAuthStore(s => s.token)
  const location = useLocation()
  if (token) return children
  // Preserve the intended destination (path + query) so /login and
  // /register can send the user there after auth completes.
  const next = `${location.pathname}${location.search}`
  const to = next && next !== '/'
    ? `/login?next=${encodeURIComponent(next)}`
    : '/login'
  return <Navigate to={to} replace />
}

// Send brand-new users (no completed/dismissed onboarding) to the wizard
// before they see anything else. Once they finish or skip, they're free
// to navigate normally — the dashboard banner picks up the skip case.
//
// Two belt-and-braces guards beyond the explicit timestamps: a user with
// a self_person_id OR with at least one tree has clearly been around the
// product before, regardless of what migration 050 set on their row.
//
// IMPORTANT: localStorage caches the user object from a previous session,
// so the gate first hits with stale data. We force a one-shot /auth/me
// refresh BEFORE deciding — otherwise an existing user without
// self_person_id (legacy account, never linked themselves) gets bounced
// into the wizard until something else triggers a refresh, which is the
// "stuck in onboarding" symptom we hit in production.
let gateRefreshDone = false  // module-level — only do this once per session
function OnboardingGate({ children }) {
  const user = useAuthStore(s => s.user)
  const refresh = useAuthStore(s => s.refresh)
  const location = useLocation()
  const [refreshed, setRefreshed] = useState(gateRefreshDone)

  useEffect(() => {
    if (refreshed) return
    refresh().finally(() => {
      gateRefreshDone = true
      setRefreshed(true)
    })
  }, [refresh, refreshed])

  // Don't redirect on first paint — wait for the refresh so we have
  // truthful data. The flicker is a single /auth/me round-trip.
  if (!refreshed) return null

  const needsOnboarding = user
    && !user.onboarding_completed_at
    && !user.onboarding_dismissed_at
    && !user.self_person_id
    && !user.has_trees
  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }
  return children
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />

      <Route path="/onboarding" element={
        <PrivateRoute>
          <OnboardingPage />
        </PrivateRoute>
      } />

      <Route path="/" element={
        <PrivateRoute>
          <OnboardingGate>
            <AppShell />
          </OnboardingGate>
        </PrivateRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="tree/:treeId" element={<TreePage />} />
        <Route path="tree/:treeId/person/:personId" element={<PersonPage />} />
        <Route path="tree/:treeId/add-person" element={<AddPersonPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="changelog" element={<ChangelogPage />} />
        <Route path="archive" element={<ArchivePage />} />
        <Route path="journey" element={<MyJourneyPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="claim-record" element={<ClaimRecordPage />} />
        <Route path="suggestions" element={<SuggestionsInboxPage />} />
        <Route path="mysteries" element={<MysteriesPage />} />
        <Route path="researcher/:slug" element={<ResearcherProfilePage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="world-map" element={<WorldMapPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
